import { Icon, IconVariant } from "../../Atoms/Icons/Icon/Icon";
import { IconTheme } from "../../Atoms/Icons/IconInfoPage/IconType";

import styles from "./Tag.module.scss";

type TagSize = "lg" | "md" | "sm" | "xs";

/**
 * Interface representing the properties for the Tag component.
 */
interface ITagProps {
    /**
     * The text to be displayed as the label.
     */
    label: string;

    /**
     * The variant of the icon, which can be used to apply different styles
     * or themes to the icon.
     * Default value is "brand".
     */
    variant?: IconVariant;

    /**
     * The position of the icon relative to the label.
     * This value is used only when the iconType is given.
     * Default value is "left".
     */
    iconPosition?: "left" | "right";

    /**
     * The size of the component. This can be used to set different sizes
     * for the icon such as xs, sm, md, or lg.
     * Default value is "md".
     */
    size?: TagSize;

    /**
     * The theme of the component, which can be used to apply different color schemes
     * or styles based on the theme.
     * Default value is "light".
     */
    theme?: IconTheme;

    /**
     * The component that will be rendered on the left or right of the label in the tag.
     */
    children?: JSX.Element;
}

function Tag({
    label,
    variant = "brand",
    children,
    iconPosition = "left",
    size = "md",
    theme = "light"
}: ITagProps): JSX.Element {
    if (!label) {
        return null;
    }

    return (
        <span className={`${styles.tag} ${styles[variant]} ${styles[theme]} ${styles[size]} ${(!children || iconPosition === "left") ? "" : styles.right}`}>
            {children && (
                <span className={styles.icon}>
                    <Icon size={size}>
                        {children}
                    </Icon>
                </span>
            )}
            <span className={styles.label} title={label}>{label}</span>
        </span>
    );
}

export { Tag };
